import React, { useEffect, useRef } from 'react';
import { ParticleNetwork } from './ParticleNetwork';
import styles from './AnalysisLoadingAnimation.module.css';
import { GapSenseLogo } from '@/components/layout/icon/AppLogo';

interface AnalysisLoadingAnimationProps {
  analysisName?: string;
}

export const AnalysisLoadingAnimation: React.FC<
  AnalysisLoadingAnimationProps
> = ({ analysisName }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const networkRef = useRef<ParticleNetwork | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize particle network
    networkRef.current = new ParticleNetwork(canvasRef.current, {
      particleCount: 100,
      connectionDistance: 100,
      particleSpeed: 0.3,
      colors: ['#4270f7', '#2bd6d1', '#5aa0ff']
    });

    networkRef.current.start();

    // Mouse tracking for interactive particles
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect && networkRef.current) {
        networkRef.current.setMousePosition(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    };

    // Handle window resize
    const handleResize = () => {
      networkRef.current?.resize();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      networkRef.current?.destroy();
    };
  }, []);

  return (
    <div className={styles.container}>
      <canvas ref={canvasRef} className={styles.canvas} />

      <div className={styles.content}>
        <div className={styles.logoWrapper}>
          <GapSenseLogo size="lg" showText={false} animated={false} />
        </div>

        <h2 className={styles.title}>Analyzing Documents</h2>

        {analysisName && <p className={styles.analysisName}>{analysisName}</p>}

        <p className={styles.subtitle}>
          This may take a few moments
          <span className={styles.dots}>
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </p>
      </div>
    </div>
  );
};
